import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { proposalsAPI, projectsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Plus, Trash2, Pencil, Loader2, Search, FileText, Eye, ArrowRightLeft, DollarSign, Download
} from 'lucide-react';
import { generateProposalPDF } from '../lib/pdfExport';
import SignatureDialog from '../components/SignatureDialog';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
  converted: 'bg-purple-100 text-purple-700',
};

const fmt = (n) => typeof n === 'number' ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';

export default function Proposals() {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [proposals, setProposals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [signOpen, setSignOpen] = useState(false);

  const makeItem = () => ({ _key: `it_${Math.random().toString(36).slice(2, 11)}`, description: '', quantity: 1, trade_price: '', markup_percentage: 30 });
  const emptyForm = {
    title: '', project_id: '', client_name: '', items: [makeItem()],
    tax_rate: 18, notes: '', terms: 'Payment due within 15 days of approval.', status: 'draft', valid_until: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    Promise.all([proposalsAPI.list(), projectsAPI.list()])
      .then(([pRes, projRes]) => { setProposals(pRes.data); setProjects(projRes.data); })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = { ...form, items: form.items.filter(i => i.description.trim()) };
      if (selected) {
        await proposalsAPI.update(selected.proposal_id, payload);
      } else {
        await proposalsAPI.create(payload);
      }
      const res = await proposalsAPI.list();
      setProposals(res.data);
      setDialogOpen(false);
      setSelected(null);
      setForm(emptyForm);
    } catch (err) { console.error(err); } finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    try { await proposalsAPI.delete(id); setProposals(proposals.filter(p => p.proposal_id !== id)); } catch (e) { console.error(e); }
  };

  const handleConvert = async (id) => {
    try {
      await proposalsAPI.convertToInvoice(id);
      const res = await proposalsAPI.list();
      setProposals(res.data);
    } catch (e) { console.error(e); }
  };

  const openEdit = (p) => {
    setSelected(p);
    setForm({
      title: p.title, project_id: p.project_id || '', client_name: p.client_name || '',
      items: p.items?.length > 0 ? p.items.map(it => ({ ...it, _key: it._key || `it_${Math.random().toString(36).slice(2, 11)}` })) : [makeItem()],
      tax_rate: p.tax_rate || 0, notes: p.notes || '', terms: p.terms || '',
      status: p.status, valid_until: p.valid_until ? p.valid_until.split('T')[0] : '',
    });
    setDialogOpen(true);
  };

  const addItem = () => setForm({ ...form, items: [...form.items, makeItem()] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };

  // Live calculations
  const subtotalTrade = form.items.reduce((s, i) => s + (parseFloat(i.trade_price) || 0) * (parseInt(i.quantity) || 1), 0);
  const subtotalClient = form.items.reduce((s, i) => {
    const trade = parseFloat(i.trade_price) || 0;
    const markup = parseFloat(i.markup_percentage) || 0;
    return s + trade * (1 + markup / 100) * (parseInt(i.quantity) || 1);
  }, 0);
  const totalMarkup = subtotalClient - subtotalTrade;
  const tax = subtotalClient * (parseFloat(form.tax_rate) || 0) / 100;
  const grandTotal = subtotalClient + tax;

  const filtered = proposals.filter(p => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return p.title?.toLowerCase().includes(t) || p.client_name?.toLowerCase().includes(t) || p.project_name?.toLowerCase().includes(t);
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="proposals-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight flex items-center gap-3">
              <FileText className="h-7 w-7 text-muted-foreground" />Proposals
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Create proposals with trade pricing and markup</p>
          </div>
          {canEdit && (
            <Button onClick={() => { setForm(emptyForm); setSelected(null); setDialogOpen(true); }} data-testid="create-proposal-btn">
              <Plus className="h-4 w-4 mr-2" />New Proposal
            </Button>
          )}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground">Total</p><p className="text-xl font-light">{proposals.length}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground">Approved</p><p className="text-xl font-light text-green-600">{proposals.filter(p => p.status === 'approved').length}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground">Total Value</p><p className="text-xl font-light">{fmt(proposals.reduce((s, p) => s + (p.grand_total || 0), 0))}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground">Total Markup</p><p className="text-xl font-light text-green-600">{fmt(proposals.reduce((s, p) => s + (p.total_markup || 0), 0))}</p></CardContent></Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search proposals..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="proposal-search" />
        </div>

        {/* Table */}
        {loading ? (
          <div className="animate-pulse space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-muted rounded" />)}</div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No proposals found</p></CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Title</TableHead>
                    <TableHead className="text-xs">Client</TableHead>
                    <TableHead className="text-xs">Project</TableHead>
                    <TableHead className="text-xs text-right">Client Total</TableHead>
                    <TableHead className="text-xs text-right">Markup</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(p => (
                    <TableRow key={p.proposal_id} data-testid={`proposal-row-${p.proposal_id}`}>
                      <TableCell className="font-medium text-sm">{p.title}</TableCell>
                      <TableCell className="text-sm">{p.client_name || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.project_name || '—'}</TableCell>
                      <TableCell className="text-right font-medium text-sm">{fmt(p.grand_total)}</TableCell>
                      <TableCell className="text-right text-sm text-green-600">{fmt(p.total_markup)}</TableCell>
                      <TableCell>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[p.status] || STATUS_STYLES.draft}`}>{p.status}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelected(p); setViewOpen(true); }}><Eye className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => generateProposalPDF(p)} title="Export PDF" data-testid={`pdf-proposal-${p.proposal_id}`}><Download className="h-3.5 w-3.5" /></Button>
                          {canEdit && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                              {p.status === 'approved' && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" onClick={() => handleConvert(p.proposal_id)} title="Convert to Invoice"><ArrowRightLeft className="h-3.5 w-3.5" /></Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(p.proposal_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* View Dialog */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>{selected?.title}</DialogTitle></DialogHeader>
            {selected && (
              <div className="space-y-4 py-2" data-testid="proposal-view">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-muted-foreground">Client</p><p className="font-medium">{selected.client_name || '—'}</p></div>
                  <div><p className="text-muted-foreground">Status</p><p><span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${STATUS_STYLES[selected.status]}`}>{selected.status}</span></p></div>
                </div>
                <div className="border-t pt-3">
                  <p className="text-sm font-medium mb-2">Items</p>
                  {(selected.items || []).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1.5 border-b last:border-0">
                      <span>{item.description} x{item.quantity || 1}</span>
                      <div className="text-right">
                        <p className="font-medium">{fmt(item.total_client)}</p>
                        <p className="text-xs text-muted-foreground">Trade: {fmt(item.total_trade)} | +{item.markup_percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-3 text-right space-y-1">
                  <p className="text-sm">Trade Total: {fmt(selected.subtotal_trade)}</p>
                  <p className="text-sm">Client Subtotal: {fmt(selected.subtotal_client)}</p>
                  <p className="text-sm text-green-600">Markup: {fmt(selected.total_markup)}</p>
                  {selected.tax_amount > 0 && <p className="text-sm">Tax ({selected.tax_rate}%): {fmt(selected.tax_amount)}</p>}
                  <p className="text-lg font-bold">Grand Total: {fmt(selected.grand_total)}</p>
                </div>
                {/* Signature */}
                {selected.signature ? (
                  <div className="border-t pt-3">
                    <p className="text-sm font-medium mb-2">Client Signature</p>
                    <img src={selected.signature} alt="Signature" className="h-16 border rounded bg-white" />
                    <p className="text-xs text-muted-foreground mt-1">
                      Signed by {selected.signed_by_name} on {selected.signed_at ? new Date(selected.signed_at).toLocaleString() : ''}
                    </p>
                  </div>
                ) : (
                  <div className="border-t pt-3 flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Not yet signed</p>
                    <Button size="sm" onClick={() => setSignOpen(true)} data-testid="sign-proposal-btn">
                      Sign & Approve
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Signature Dialog */}
        {selected && (
          <SignatureDialog
            open={signOpen}
            onOpenChange={setSignOpen}
            documentType="proposal"
            documentId={selected.proposal_id}
            onSigned={async () => {
              const res = await proposalsAPI.list();
              setProposals(res.data);
              const updated = res.data.find(p => p.proposal_id === selected.proposal_id);
              if (updated) setSelected(updated);
            }}
          />
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selected ? 'Edit Proposal' : 'Create Proposal'}</DialogTitle>
              <DialogDescription>Build a proposal with trade pricing and client markup</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="Design Proposal" data-testid="proposal-title-input" /></div>
                  <div className="space-y-2"><Label>Client Name</Label><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="Client name" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Project</Label>
                    <Select value={form.project_id || 'none'} onValueChange={v => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No Project</SelectItem>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{['draft','sent','approved','rejected'].map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  <Label>Line Items (Trade + Markup)</Label>
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground px-1">
                      <div className="col-span-4">Description</div><div className="col-span-1">Qty</div><div className="col-span-2">Trade ₹</div><div className="col-span-2">Markup %</div><div className="col-span-2 text-right">Client ₹</div><div className="col-span-1"></div>
                    </div>
                    {form.items.map((item, idx) => {
                      const clientPrice = (parseFloat(item.trade_price) || 0) * (1 + (parseFloat(item.markup_percentage) || 0) / 100);
                      return (
                        <div key={item._key || idx} className="grid grid-cols-12 gap-2 items-center">
                          <Input className="col-span-4 h-8 text-sm" placeholder="Item description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} data-testid={`proposal-item-desc-${idx}`} />
                          <Input className="col-span-1 h-8 text-sm" type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} />
                          <Input className="col-span-2 h-8 text-sm" type="number" step="0.01" placeholder="Trade" value={item.trade_price} onChange={e => updateItem(idx, 'trade_price', e.target.value)} data-testid={`proposal-item-trade-${idx}`} />
                          <Input className="col-span-2 h-8 text-sm" type="number" step="0.1" value={item.markup_percentage} onChange={e => updateItem(idx, 'markup_percentage', e.target.value)} />
                          <div className="col-span-2 text-right text-sm font-medium">{fmt(clientPrice * (parseInt(item.quantity) || 1))}</div>
                          {form.items.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8 text-destructive" onClick={() => removeItem(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                          )}
                        </div>
                      );
                    })}
                    <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" />Add Item</Button>
                  </div>
                </div>

                {/* Totals */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Tax Rate (%)</Label><Input type="number" min="0" max="100" step="0.1" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: e.target.value })} /></div>
                  <div className="space-y-1 text-right pt-4">
                    <p className="text-sm text-muted-foreground">Trade: {fmt(subtotalTrade)}</p>
                    <p className="text-sm">Client Subtotal: {fmt(subtotalClient)}</p>
                    <p className="text-sm text-green-600">Markup: {fmt(totalMarkup)}</p>
                    {tax > 0 && <p className="text-sm text-muted-foreground">Tax: {fmt(tax)}</p>}
                    <p className="text-lg font-bold">Total: {fmt(grandTotal)}</p>
                  </div>
                </div>

                <div className="space-y-2"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." /></div>
                <div className="space-y-2"><Label>Terms</Label><Input value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} data-testid="save-proposal-btn">
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {selected ? 'Update' : 'Create Proposal'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
